import { useEffect, useRef, useState } from 'react';
import { useSpeaker } from '../hooks/SpeakerProvider.tsx';
import { useTickSound } from '../hooks/useTickSound.ts';

/**
 * Grosse, ablaufende Anzeige fuer eine Phase mit Frist.
 *
 * Gebaut fuer das Klau-Fenster. Dort standen fuenfzehn Sekunden Druck
 * bisher als vierzehn Pixel Text in der Kopfzeile — waehrend der Blick
 * auf der Zeitleiste in der Bildmitte lag. Die unauffaelligste Stelle
 * des Bildschirms vermittelte den einzigen echten Zeitdruck im Spiel.
 *
 * Der Balken ist hier wichtiger als die Zahl: eine schrumpfende Strecke
 * sieht man aus dem Augenwinkel, eine Ziffer muss man lesen.
 *
 * Ergaenzt den kleinen `Countdown` in der Kopfzeile, ersetzt ihn nicht
 * — der gilt fuer alle Phasen mit Frist, dieser nur fuer die eine, in
 * der es wirklich um Sekunden geht.
 */

/** Ab hier wird es knapp: rote Faerbung und ein Ton pro Sekunde. */
const URGENT_SECONDS = 5;

export function PhaseTimer({
  deadline,
  totalMs,
  label,
}: {
  deadline: number | null;
  /** Volle Dauer der Phase — daraus ergibt sich der Balkenanteil. */
  totalMs: number;
  label?: string;
}) {
  const [remaining, setRemaining] = useState(() =>
    deadline === null ? 0 : Math.max(0, deadline - Date.now()),
  );

  const speaker = useSpeaker();
  const { tick } = useTickSound(speaker.volume);

  /**
   * Zuletzt vertonte Sekunde.
   *
   * Ohne das feuert der Ton bei jedem Bild — sechzig Klicks pro
   * Sekunde statt einem.
   */
  const lastBeepRef = useRef<number | null>(null);

  useEffect(() => {
    if (deadline === null) return;

    let frame = 0;

    const step = () => {
      const left = Math.max(0, deadline - Date.now());
      setRemaining(left);

      const seconds = Math.ceil(left / 1000);
      if (seconds <= URGENT_SECONDS && seconds > 0 && lastBeepRef.current !== seconds) {
        lastBeepRef.current = seconds;
        // Steigt zum Ende hin an: 1 s vor Schluss am hoechsten.
        tick(1 - (seconds - 1) / URGENT_SECONDS);
      }

      if (left > 0) frame = requestAnimationFrame(step);
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
    // `tick` ist stabil (useCallback ohne Abhaengigkeiten).
  }, [deadline, tick]);

  if (deadline === null) return null;

  const seconds = Math.ceil(remaining / 1000);
  const urgent = seconds <= URGENT_SECONDS;
  // Gegen 0 geteilt wuerde NaN ergeben und den Balken verschwinden lassen.
  const fraction = totalMs > 0 ? Math.min(1, Math.max(0, remaining / totalMs)) : 0;

  return (
    <div className={`phase-timer ${urgent ? 'phase-timer--urgent' : ''}`}>
      <div className="phase-timer__head">
        {label && <span className="phase-timer__label">{label}</span>}
        {/*
         * `aria-hidden`: Die Zahl aendert sich jede Sekunde. Als
         * Live-Region gelesen waere sie ein Sekundentakt-Diktat. Die
         * Frist steht im Text daneben.
         */}
        <span className="phase-timer__seconds" aria-hidden="true">
          {seconds}
        </span>
      </div>

      <div className="phase-timer__track" aria-hidden="true">
        <div className="phase-timer__fill" style={{ transform: `scaleX(${fraction})` }} />
      </div>
    </div>
  );
}
